import { createSlice } from '@reduxjs/toolkit';
import { tokenService } from 'services/api/tokenService';
import { login, logout, restoreSession } from './authThunks';

/**
 * Auth state. `status` distinguishes the initial "restoring session" phase
 * from idle so the app can show a splash loader instead of flashing the login
 * screen on refresh.
 */
const initialState = {
  user: null,
  isAuthenticated: false,
  status: tokenService.hasSession() ? 'restoring' : 'idle',
  loginLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Triggered by the session-expired event from the axios layer.
    sessionExpired(state) {
      state.user = null;
      state.isAuthenticated = false;
      state.status = 'idle';
    },
    clearAuthError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loginLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loginLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.status = 'idle';
      })
      .addCase(login.rejected, (state, action) => {
        state.loginLoading = false;
        state.error = action.payload?.message || 'Login failed';
      })

      .addCase(restoreSession.pending, (state) => {
        state.status = 'restoring';
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.status = 'idle';
      })
      .addCase(restoreSession.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.status = 'idle';
      })

      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.status = 'idle';
      });
  },
});

export const { sessionExpired, clearAuthError } = authSlice.actions;
export default authSlice.reducer;
